// lib/pwa.ts — Service Worker registration + offline status hook

import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Types for vite-plugin-pwa's virtual module
// ---------------------------------------------------------------------------

interface RegisterSWOptions {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
  onRegistered?: (registration: ServiceWorkerRegistration | undefined) => void;
  onRegisterError?: (error: unknown) => void;
}

// ---------------------------------------------------------------------------
// Lazy-load the virtual SW module injected by vite-plugin-pwa at build time.
// During dev (when devOptions.enabled = false) the module does not exist,
// so we fall back gracefully.
// ---------------------------------------------------------------------------

let swUpdateCallback: (() => void) | null = null;

export async function initPWA(onNeedRefresh: () => void): Promise<void> {
  swUpdateCallback = onNeedRefresh;

  // In dev mode vite-plugin-pwa does not inject the virtual module.
  if (import.meta.env.DEV) return;

  try {
    // Dynamic import to avoid breaking dev build
    const { registerSW } = await import('virtual:pwa-register');

    registerSW({
      onNeedRefresh() {
        swUpdateCallback?.();
      },
      onOfflineReady() {
        console.info('[PWA] App is ready to work offline.');
      },
      onRegistered(registration) {
        console.info('[PWA] Service worker registered:', registration?.scope);
      },
      onRegisterError(error) {
        console.warn('[PWA] Service worker registration failed:', error);
      },
    } as RegisterSWOptions);
  } catch (e) {
    // Module not available in dev — ignore silently
    console.debug('[PWA] SW registration skipped (dev mode or build not available).');
  }
}

/** Trigger the waiting service worker to activate (used by update prompt). */
export function applyPWAUpdate(): void {
  // The cleanest way to activate a waiting SW without a custom SW file
  // is to send a SKIP_WAITING message then reload.
  navigator.serviceWorker?.getRegistration().then((reg) => {
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    // Brief delay to allow the new SW to claim clients
    setTimeout(() => window.location.reload(), 250);
  });
}

// ---------------------------------------------------------------------------
// useOfflineStatus — React hook
// ---------------------------------------------------------------------------

/**
 * Returns live online/offline status.
 * Initialises from `navigator.onLine` and updates on network events.
 */
export function useOfflineStatus(): { isOnline: boolean } {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline };
}
